// Utilities for constructing shaders that
// handle the coloring of Neuroglancer point annotation layers.
// References:
// - https://github.com/vitessce/vitessce/issues/2359#issuecomment-3572906947
// - https://chanzuckerberg.github.io/cryoet-data-portal/stable/neuroglancer_quickstart.html
import { PALETTE, getDefaultColor } from '@vitessce/utils';
import { colormaps, GLSL_COLORMAPS, GLSL_COLORMAP_DEFAULT } from '@vitessce/gl';
/**
 * Normalize an RGB color array from [0, 255] to [0, 1].
 * @param {[number, number, number]} rgbColor
 * @returns {[number, number, number]}
 */
function normalizeColor(rgbColor) {
    return rgbColor.map(c => c / 255);
}
/**
 * GLSL call to set point marker border width.
 * Set to 0.0 to remove the outline.
 * @param {number} borderWidth
 * @returns {string}
 */
function borderWidthGlsl(borderWidth = 0.0) {
    // must be decimal/float value
    return `setPointMarkerBorderWidth(${borderWidth.toFixed(1)});`;
}
/**
 * Format a normalized color as a GLSL vec3 literal.
 * @param {[number, number, number]} normalizedColor
 * @returns {string}
 */
function toVec3(normalizedColor) {
    return `vec3(${normalizedColor.join(', ')})`;
}
/**
 * Format a normalized color as a GLSL vec4 literal.
 * @param {[number, number, number]} normalizedColor
 * @param {number} alpha
 * @returns {string}
 */
function toVec4(normalizedColor, alpha) {
    return `vec4(${normalizedColor.join(', ')}, ${alpha})`;
}
// ============================================================
// Case 1: spatialLayerColor
// ============================================================
/**
 * Generate a shader for spatialLayerColor encoding with no feature selection.
 * All points get the static color.
 * @param {[number, number, number]} staticColor RGB (0-255).
 * @param {number} opacity Opacity (0-1).
 * @returns {string} A GLSL shader string.
 */
export function getSpatialLayerColorShader(staticColor, opacity, borderWidth = 0.0) {
    const norm = normalizeColor(staticColor);
    // lang: glsl
    return `
        void main() {
            setColor(${toVec4(norm, opacity)});
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
/**
 * Generate a shader for spatialLayerColor encoding with feature selection.
 * Selected features get the static color; unselected get the default color.
 * @param {[number, number, number]} staticColor RGB (0-255).
 * @param {number} opacity Opacity (0-1).
 * @param {number[]} featureIndices Numeric indices of selected features.
 * @param {[number, number, number]} defaultColor RGB (0-255) for unselected points.
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getSpatialLayerColorWithSelectionShader(staticColor, opacity, featureIndices, defaultColor, featureIndexProp, borderWidth = 0.0) {
    const normStatic = normalizeColor(staticColor);
    const normDefault = normalizeColor(defaultColor);
    const numFeatures = featureIndices.length;
    const indicesArr = `int selectedIndices[${numFeatures}] = int[${numFeatures}](${featureIndices.join(', ')});`;
    // lang: glsl
    return `
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            ${indicesArr}
            bool isSelected = false;
            for (int i = 0; i < ${numFeatures}; ++i) {
                if (geneIndex == selectedIndices[i]) {
                    isSelected = true;
                }
            }
            if (isSelected) {
                setColor(${toVec4(normStatic, opacity)});
                ${borderWidthGlsl(borderWidth)}
            } else {
                setColor(${toVec4(normDefault, opacity)});
                ${borderWidthGlsl(borderWidth)}
            }
        }
    `;
}
export function getObsSetSelectionFilteredShader(phenotypeColors, selectedIndices, opacity, borderWidth = 0.0) {
    const numColors = phenotypeColors.length;
    const numSelected = selectedIndices.length;
    const normColors = phenotypeColors.map(c => normalizeColor(c));
    const colorsDecl = `vec3 phenotypeColors[${numColors}] = vec3[${numColors}](${normColors.map(c => toVec3(c)).join(', ')});`;
    const selectedDecl = `int selectedIndices[${numSelected}] = int[${numSelected}](${selectedIndices.join(', ')});`;
    return `
    void main() {
      int phenotype = prop_phenotype();
      ${colorsDecl}
      ${selectedDecl}
      bool isSelected = false;
      for (int i = 0; i < ${numSelected}; ++i) {
        if (phenotype == selectedIndices[i]) {
          isSelected = true;
        }
      }
      if (!isSelected) {
        discard;
      }
      vec3 color = vec3(0.5, 0.5, 0.5);
      if (phenotype >= 0 && phenotype < ${numColors}) {
        color = phenotypeColors[phenotype];
      }
      setColor(vec4(color, ${opacity.toFixed(4)}));
      ${borderWidthGlsl(borderWidth)}
    }
  `;
}
/**
 * Generate a shader for spatialLayerColor encoding with feature selection
 * and featureFilterMode='featureSelection' (hide unselected points).
 * @param {[number, number, number]} staticColor RGB (0-255).
 * @param {number} opacity Opacity (0-1).
 * @param {number[]} featureIndices Numeric indices of selected features.
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getSpatialLayerColorFilteredShader(staticColor, opacity, featureIndices, featureIndexProp, borderWidth = 0.0) {
    const normStatic = normalizeColor(staticColor);
    const numFeatures = featureIndices.length;
    const indicesArr = `int selectedIndices[${numFeatures}] = int[${numFeatures}](${featureIndices.join(', ')});`;
    // lang: glsl
    return `
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            ${indicesArr}
            bool isSelected = false;
            for (int i = 0; i < ${numFeatures}; ++i) {
                if (geneIndex == selectedIndices[i]) {
                    isSelected = true;
                }
            }
            if (!isSelected) {
                discard;
            }
            setColor(${toVec4(normStatic, opacity)});
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
// ============================================================
// Case 2: geneSelection
// ============================================================
/**
 * Generate a shader for geneSelection encoding with no feature selection.
 * All points get the static color (since no features are selected to
 * determine per-feature colors).
 * @param {[number, number, number]} staticColor RGB (0-255).
 * @param {number} opacity Opacity (0-1).
 * @returns {string} A GLSL shader string.
 */
export function getGeneSelectionNoSelectionShader(staticColor, opacity, borderWidth = 0.0) {
    const norm = normalizeColor(staticColor);
    // lang: glsl
    return `
        void main() {
            setColor(${toVec4(norm, opacity)});
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
/**
 * Generate a shader for geneSelection encoding with feature selection.
 * Each selected feature gets its color from featureColor; unselected
 * points get the default color.
 * @param {number[]} featureIndices Numeric indices of selected features.
 * @param {[number, number, number][]} featureColors RGB (0-255) for each
 *   selected feature, in the same order as featureIndices.
 * @param {[number, number, number]} staticColor Fallback RGB (0-255)
 *   for selected features without a specified color.
 * @param {[number, number, number]} defaultColor RGB (0-255) for
 *   unselected points.
 * @param {number} opacity Opacity (0-1).
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getGeneSelectionWithSelectionShader(featureIndices, featureColors, staticColor, defaultColor, opacity, featureIndexProp, borderWidth = 0.0) {
    const numFeatures = featureIndices.length;
    const normDefault = normalizeColor(defaultColor);
    const normColors = featureColors.map(c => normalizeColor(c));
    const normStatic = normalizeColor(staticColor);
    const colorArr = normColors.map(c => (c ? toVec3(c) : toVec3(normStatic)));
    const indicesDecl = `int selectedIndices[${numFeatures}] = int[${numFeatures}](${featureIndices.join(', ')});`;
    const colorsDecl = `vec3 featureColors[${numFeatures}] = vec3[${numFeatures}](${colorArr.join(', ')});`;
    // lang: glsl
    return `
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            ${indicesDecl}
            ${colorsDecl}
            vec4 color = ${toVec4(normDefault, opacity)};
            for (int i = 0; i < ${numFeatures}; ++i) {
                if (geneIndex == selectedIndices[i]) {
                    color = vec4(featureColors[i], ${opacity});
                }
            }
            setColor(color);
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
/**
 * Generate a shader for geneSelection encoding with feature selection
 * and featureFilterMode='featureSelection' (hide unselected points).
 * @param {number[]} featureIndices Numeric indices of selected features.
 * @param {[number, number, number][]} featureColors RGB (0-255) for each
 *   selected feature.
 * @param {[number, number, number]} staticColor Fallback RGB (0-255).
 * @param {number} opacity Opacity (0-1).
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getGeneSelectionFilteredShader(featureIndices, featureColors, staticColor, opacity, featureIndexProp, borderWidth = 0.0) {
    const numFeatures = featureIndices.length;
    const normColors = featureColors.map(c => normalizeColor(c));
    const normStatic = normalizeColor(staticColor);
    const colorArr = normColors.map(c => (c ? toVec3(c) : toVec3(normStatic)));
    const indicesDecl = `int selectedIndices[${numFeatures}] = int[${numFeatures}](${featureIndices.join(', ')});`;
    const colorsDecl = `vec3 featureColors[${numFeatures}] = vec3[${numFeatures}](${colorArr.join(', ')});`;
    // lang: glsl
    return `
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            ${indicesDecl}
            ${colorsDecl}
            bool isSelected = false;
            vec3 matchedColor = vec3(0.0);
            for (int i = 0; i < ${numFeatures}; ++i) {
                if (geneIndex == selectedIndices[i]) {
                    isSelected = true;
                    matchedColor = featureColors[i];
                }
            }
            if (!isSelected) {
                discard;
            }
            setColor(vec4(matchedColor, ${opacity}));
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
// ============================================================
// Case 3: randomByFeature
// ============================================================
/**
 * Generate a shader for randomByFeature encoding with no feature selection.
 * Each feature gets a deterministic color from PALETTE based on its index.
 * @param {number} opacity Opacity (0-1).
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getRandomByFeatureShader(opacity, featureIndexProp, borderWidth = 0.0) {
    const paletteSize = PALETTE.length;
    const normPalette = PALETTE.map(c => normalizeColor(c));
    const paletteDecl = `vec3 palette[${paletteSize}] = vec3[${paletteSize}](${normPalette.map(c => toVec3(c)).join(', ')});`;
    // lang: glsl
    return `
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            ${paletteDecl}
            int colorIdx = geneIndex - (geneIndex / ${paletteSize}) * ${paletteSize};
            if (colorIdx < 0) { colorIdx = -colorIdx; }
            vec3 color = palette[colorIdx];
            setColor(vec4(color, ${opacity}));
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
/**
 * Generate a shader for randomByFeature encoding with feature selection.
 * Selected features get their deterministic palette color; unselected
 * points get the default color.
 * @param {number[]} featureIndices Numeric indices of selected features.
 * @param {[number, number, number]} defaultColor RGB (0-255) for unselected.
 * @param {number} opacity Opacity (0-1).
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getRandomByFeatureWithSelectionShader(featureIndices, defaultColor, opacity, featureIndexProp, borderWidth = 0.0) {
    const paletteSize = PALETTE.length;
    const normPalette = PALETTE.map(c => normalizeColor(c));
    const normDefault = normalizeColor(defaultColor);
    const numFeatures = featureIndices.length;
    const paletteDecl = `vec3 palette[${paletteSize}] = vec3[${paletteSize}](${normPalette.map(c => toVec3(c)).join(', ')});`;
    const indicesDecl = `int selectedIndices[${numFeatures}] = int[${numFeatures}](${featureIndices.join(', ')});`;
    // lang: glsl
    return `
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            ${paletteDecl}
            ${indicesDecl}
            bool isSelected = false;
            for (int i = 0; i < ${numFeatures}; ++i) {
                if (geneIndex == selectedIndices[i]) {
                    isSelected = true;
                }
            }
            if (isSelected) {
                int colorIdx = geneIndex - (geneIndex / ${paletteSize}) * ${paletteSize};
                if (colorIdx < 0) { colorIdx = -colorIdx; }
                setColor(vec4(palette[colorIdx], ${opacity}));
                ${borderWidthGlsl(borderWidth)}
            } else {
                setColor(${toVec4(normDefault, opacity)});
                ${borderWidthGlsl(borderWidth)}
            }
        }
    `;
}
/**
 * Generate a shader for randomByFeature encoding with feature selection
 * and featureFilterMode='featureSelection' (hide unselected points).
 * @param {number[]} featureIndices Numeric indices of selected features.
 * @param {number} opacity Opacity (0-1).
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getRandomByFeatureFilteredShader(featureIndices, opacity, featureIndexProp, borderWidth = 0.0) {
    const paletteSize = PALETTE.length;
    const normPalette = PALETTE.map(c => normalizeColor(c));
    const numFeatures = featureIndices.length;
    const paletteDecl = `vec3 palette[${paletteSize}] = vec3[${paletteSize}](${normPalette.map(c => toVec3(c)).join(', ')});`;
    const indicesDecl = `int selectedIndices[${numFeatures}] = int[${numFeatures}](${featureIndices.join(', ')});`;
    // lang: glsl
    return `
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            ${paletteDecl}
            ${indicesDecl}
            bool isSelected = false;
            for (int i = 0; i < ${numFeatures}; ++i) {
                if (geneIndex == selectedIndices[i]) {
                    isSelected = true;
                }
            }
            if (!isSelected) {
                discard;
            }
            int colorIdx = geneIndex - (geneIndex / ${paletteSize}) * ${paletteSize};
            if (colorIdx < 0) { colorIdx = -colorIdx; }
            setColor(vec4(palette[colorIdx], ${opacity}));
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
// ============================================================
// Case 4: random (per point)
// ============================================================
/**
 * GLSL helper function that produces a pseudo-random float in [0, 1]
 * from an integer value and a seed. Shared across random-per-point shaders.
 * @returns {string} GLSL function source.
 */
function hashToFloatGlsl() {
    return `
        float hashToFloat(int v, int seed) {
            int h = v ^ (seed * 16777619);
            h = h * 747796405 + 2891336453;
            h = ((h >> 16) ^ h) * 2654435769;
            h = ((h >> 16) ^ h);
            return float(h & 0x7FFFFFFF) / float(0x7FFFFFFF);
        }
    `;
}
/**
 * Generate a shader for random-per-point encoding with no feature selection.
 * Each point gets a pseudo-random color based on its index.
 * @param {number} opacity Opacity (0-1).
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @param {string} pointIndexProp The property name for the point index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getRandomPerPointShader(opacity, featureIndexProp, pointIndexProp, borderWidth = 0.0) {
    // lang: glsl
    return `
        ${hashToFloatGlsl()}
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            int pointIndex = prop_${pointIndexProp}();
            float r = hashToFloat(pointIndex, 0);
            float g = hashToFloat(pointIndex, 1);
            float b = hashToFloat(pointIndex, 2);
            setColor(vec4(r, g, b, ${opacity}));
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
// ============================================================
// Case 5: quantitativeColormap (continuous/numeric coloring)
// ============================================================
/**
 * Generate a shader for quantitative colormap encoding.
 * Colors each point by mapping a numeric annotation property through a colormap.
 * @param {string} quantitativeColorProp - Annotation property name holding the numeric value.
 * @param {number} opacity
 * @param {string} colormap - Must be a value from GLSL_COLORMAPS e.g. 'viridis', 'plasma'
 * @param {[number, number]} colormapRange - [low, high] raw value range to normalize
 * @param {number} borderWidth
 */
export function getQuantitativeColormapShader(quantitativeColorProp, opacity, colormap = GLSL_COLORMAP_DEFAULT, colormapRange = [0.0, 1.0], borderWidth = 0.0, quantitativeColorMax = 1.0) {
    const [low, high] = colormapRange;
    const resolvedColormap = GLSL_COLORMAPS.includes(colormap) ? colormap : GLSL_COLORMAP_DEFAULT;
    // lang: glsl
    return `
    #define COLORMAP_FUNC ${resolvedColormap}
    ${colormaps}
    void main() {
      float rawVal = float(prop_${quantitativeColorProp}());
      float normalizedVal = rawVal / ${quantitativeColorMax.toFixed(4)};
      float t = (normalizedVal - ${low.toFixed(4)}) / max(${(high - low).toFixed(4)}, 0.0001);
      t = clamp(t, 0.0, 1.0);
      vec4 col = COLORMAP_FUNC(t);
      setColor(vec4(col.rgb, ${opacity.toFixed(4)}));
      ${borderWidthGlsl(borderWidth)}
    }
  `;
}
/**
 * Generate a shader for random-per-point encoding with feature selection.
 * Selected points get a pseudo-random color; unselected get the default color.
 * @param {number[]} featureIndices Numeric indices of selected features.
 * @param {[number, number, number]} defaultColor RGB (0-255) for unselected.
 * @param {number} opacity Opacity (0-1).
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @param {string} pointIndexProp The property name for the point index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getRandomPerPointWithSelectionShader(featureIndices, defaultColor, opacity, featureIndexProp, pointIndexProp, borderWidth = 0.0) {
    const normDefault = normalizeColor(defaultColor);
    const numFeatures = featureIndices.length;
    const indicesDecl = `int selectedIndices[${numFeatures}] = int[${numFeatures}](${featureIndices.join(', ')});`;
    // lang: glsl
    return `
        ${hashToFloatGlsl()}
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            int pointIndex = prop_${pointIndexProp}();
            ${indicesDecl}
            bool isSelected = false;
            for (int i = 0; i < ${numFeatures}; ++i) {
                if (geneIndex == selectedIndices[i]) {
                    isSelected = true;
                }
            }
            if (isSelected) {
                float r = hashToFloat(pointIndex, 0);
                float g = hashToFloat(pointIndex, 1);
                float b = hashToFloat(pointIndex, 2);
                setColor(vec4(r, g, b, ${opacity}));
                ${borderWidthGlsl(borderWidth)}
            } else {
                setColor(${toVec4(normDefault, opacity)});
                ${borderWidthGlsl(borderWidth)}
            }
        }
    `;
}
/**
 * Generate a shader for random-per-point encoding with feature selection
 * and featureFilterMode='featureSelection' (hide unselected points).
 * @param {number[]} featureIndices Numeric indices of selected features.
 * @param {number} opacity Opacity (0-1).
 * @param {string} featureIndexProp The property name for the feature index in the shader.
 * @param {string} pointIndexProp The property name for the point index in the shader.
 * @returns {string} A GLSL shader string.
 */
export function getRandomPerPointFilteredShader(featureIndices, opacity, featureIndexProp, pointIndexProp, borderWidth = 0.0) {
    const numFeatures = featureIndices.length;
    const indicesDecl = `int selectedIndices[${numFeatures}] = int[${numFeatures}](${featureIndices.join(', ')});`;
    // lang: glsl
    return `
        ${hashToFloatGlsl()}
        void main() {
            int geneIndex = prop_${featureIndexProp}();
            int pointIndex = prop_${pointIndexProp}();
            ${indicesDecl}
            bool isSelected = false;
            for (int i = 0; i < ${numFeatures}; ++i) {
                if (geneIndex == selectedIndices[i]) {
                    isSelected = true;
                }
            }
            if (!isSelected) {
                discard;
            }
            float r = hashToFloat(pointIndex, 0);
            float g = hashToFloat(pointIndex, 1);
            float b = hashToFloat(pointIndex, 2);
            setColor(vec4(r, g, b, ${opacity}));
            ${borderWidthGlsl(borderWidth)}
        }
    `;
}
/**
 * Generate a shader for obsSetSelection encoding using phenotype index.
 * Colors each point by its phenotype category using colors from obsSetColor.
 * @param {Array} phenotypeColors - Array of 13 RGB colors [r,g,b] indexed by phenotype int8
 * @param {number} opacity
 * @param {number} borderWidth
 */
export function getObsSetSelectionShader(phenotypeColors, opacity, borderWidth = 0.0) {
    const numColors = phenotypeColors.length;
    const normColors = phenotypeColors.map(c => normalizeColor(c));
    const colorsDecl = `vec3 phenotypeColors[${numColors}] = vec3[${numColors}](${normColors.map(c => toVec3(c)).join(', ')});`;
    return `
    void main() {
      int phenotype = prop_phenotype();
      ${colorsDecl}
      vec3 color = ${toVec3(normalizeColor([128, 128, 128]))};
      if (phenotype >= 0 && phenotype < ${numColors}) {
        color = phenotypeColors[phenotype];
      }
      setColor(vec4(color, ${opacity.toFixed(4)}));
      ${borderWidthGlsl(borderWidth)}
    }
  `;
}
export function getPointsShader(layerCoordination) {
    const { theme, featureIndex, spatialLayerOpacity, obsColorEncoding, spatialLayerColor, featureSelection, featureFilterMode, featureColor, pointMarkerBorderWidth = 0.0, featureIndexProp, pointIndexProp, quantitativeColorProp, featureValueColormap, featureValueColormapRange, obsSetColor, obsSetSelection, quantitativeColorMax, pointsAreSegmentationCentroids, } = layerCoordination;
    const defaultColor = getDefaultColor(theme);
    const opacity = spatialLayerOpacity ?? 1.0;
    const staticColor = (Array.isArray(spatialLayerColor) && spatialLayerColor.length === 3
        ? spatialLayerColor
        : defaultColor);
    const hasFeatureSelection = (Array.isArray(featureSelection) && featureSelection.length > 0);
    const isFiltered = featureFilterMode === 'featureSelection';
    // Resolve selected feature names to numeric indices.
    let featureIndices = [];
    if (hasFeatureSelection && Array.isArray(featureIndex)) {
        featureIndices = featureSelection
            .map(name => featureIndex.indexOf(name))
            .filter(i => i >= 0);
    }
    const hasResolvedIndices = featureIndices.length > 0;
    // Resolve per-feature colors (in the same order as featureIndices).
    const resolvedFeatureColors = hasResolvedIndices
        ? featureSelection
            .filter(name => featureIndex?.indexOf(name) >= 0)
            .map((name) => {
            const match = Array.isArray(featureColor)
                ? featureColor.find(fc => fc.name === name)?.color
                : null;
            return match || staticColor;
        })
        : [];
    // Points coloring cases:
    // (See `createPointLayer` in spatial-beta/Spatial.js for more background.)
    // Coloring cases:
    // - spatialLayerColor: one color for all points.
    //   consider all as selected when featureSelection is null.
    // - spatialLayerColor with featureSelection: one color for
    //   all selected points, default color for unselected points
    // - spatialLayerColor with featureSelection and
    //   featureFilterMode 'featureSelection': one color for selected points,
    //   do not show unselected points
    // - geneSelection: use colors from "featureColor".
    //   consider all as selected when featureSelection is null.
    // - geneSelection with featureSelection: use colors from
    //   "featureColor" (array of { name, color: [r, g, b] }) for
    //   selected features, default color for unselected points
    // - geneSelection with featureFilterMode 'featureSelection': use colors
    //   from "featureColor" for selected features,
    //   do not show unselected points
    // - randomByFeature: random color for each feature
    //   (deterministic based on feature index).
    //   consider all as selected when featureSelection is null.
    // - randomByFeature with preferFeatureColor: use colors from
    //   "featureColor" (array of { name, color: [r, g, b] }) where
    //   available, and random colors otherwise.
    // - randomByFeature with featureSelection: random color for
    //   selected features, default color for unselected points
    // - randomByFeature with featureSelection and
    //   featureFilterMode 'featureSelection': random color for
    //   selected features, do not show unselected points
    // - random: random color for each point
    //   (deterministic based on point index).
    //   consider all as selected when featureSelection is null.
    // - random with preferFeatureColor: use colors from "featureColor"
    //   (array of { name, color: [r, g, b] }) where available,
    //   and random colors otherwise.
    // - random with featureSelection: random color for selected points,
    //   default color for unselected points
    // - random with featureSelection and
    //   featureFilterMode 'featureSelection': random color for selected
    //   points, do not show unselected points
    // ---- spatialLayerColor ----
    if (obsColorEncoding === 'spatialLayerColor') {
        if (!hasFeatureSelection || !hasResolvedIndices) {
            return getSpatialLayerColorShader(staticColor, opacity, pointMarkerBorderWidth);
        }
        if (isFiltered) {
            return getSpatialLayerColorFilteredShader(staticColor, opacity, featureIndices, featureIndexProp, pointMarkerBorderWidth);
        }
        return getSpatialLayerColorWithSelectionShader(staticColor, opacity, featureIndices, defaultColor, featureIndexProp, pointMarkerBorderWidth);
    }
    // ---- geneSelection ----
    if (obsColorEncoding === 'geneSelection' && !pointsAreSegmentationCentroids) {
        if (!featureIndexProp) {
            throw new Error('In order to use gene-based color encoding for Neuroglancer Points, options.featureIndexProp must be specified for the obsPoints.ng-annotations fileType in the Vitessce configuration.');
        }
        if (!hasFeatureSelection || !hasResolvedIndices) {
            return getGeneSelectionNoSelectionShader(staticColor, opacity, pointMarkerBorderWidth);
        }
        if (isFiltered) {
            return getGeneSelectionFilteredShader(featureIndices, resolvedFeatureColors, staticColor, opacity, featureIndexProp, pointMarkerBorderWidth);
        }
        return getGeneSelectionWithSelectionShader(featureIndices, resolvedFeatureColors, staticColor, defaultColor, opacity, featureIndexProp, pointMarkerBorderWidth);
    }
    // ---- randomByFeature ----
    if (obsColorEncoding === 'randomByFeature') {
        if (!featureIndexProp) {
            throw new Error('In order to use gene-based color encoding for Neuroglancer Points, options.featureIndexProp must be specified for the obsPoints.ng-annotations fileType in the Vitessce configuration.');
        }
        if (!hasFeatureSelection || !hasResolvedIndices) {
            return getRandomByFeatureShader(opacity, featureIndexProp, pointMarkerBorderWidth);
        }
        if (isFiltered) {
            return getRandomByFeatureFilteredShader(featureIndices, opacity, featureIndexProp, pointMarkerBorderWidth);
        }
        return getRandomByFeatureWithSelectionShader(featureIndices, defaultColor, opacity, featureIndexProp, pointMarkerBorderWidth);
    }
    // ---- random (per point) ----
    if (obsColorEncoding === 'random') {
        if (!pointIndexProp) {
            throw new Error('In order to use per-point color encoding for Neuroglancer Points, options.pointIndexProp must be specified for the obsPoints.ng-annotations fileType in the Vitessce configuration.');
        }
        if (!hasFeatureSelection || !hasResolvedIndices) {
            return getRandomPerPointShader(opacity, featureIndexProp, pointIndexProp, pointMarkerBorderWidth);
        }
        if (isFiltered) {
            return getRandomPerPointFilteredShader(featureIndices, opacity, featureIndexProp, pointIndexProp, pointMarkerBorderWidth);
        }
        return getRandomPerPointWithSelectionShader(featureIndices, defaultColor, opacity, featureIndexProp, pointIndexProp, pointMarkerBorderWidth);
    }
    // Quantitative color encoding for points when
    // pointsAreSegmentationCentroids is true (e.g., for segmentation centroids).
    if (obsColorEncoding === 'geneSelection' && pointsAreSegmentationCentroids) {
        if (!quantitativeColorProp) {
            console.warn('In order to use quantitative colormap encoding for Neuroglancer Points, '
                + 'options.quantitativeColorProp must be specified for the '
                + 'obsPoints.ng-annotations fileType in the Vitessce configuration. '
                + 'Falling back to static color.');
            return getSpatialLayerColorShader(staticColor, opacity, pointMarkerBorderWidth);
        }
        return getQuantitativeColormapShader(quantitativeColorProp, opacity, featureValueColormap, featureValueColormapRange, pointMarkerBorderWidth, quantitativeColorMax);
    }
    if (obsColorEncoding === 'cellSetSelection') {
        if (!obsSetColor || !obsSetSelection) {
            // Fall back to static grey until obs sets load
            return getSpatialLayerColorShader(staticColor, opacity, pointMarkerBorderWidth);
        }
        const colorByName = {};
        obsSetColor?.forEach(({ path, color }) => {
            // Only include leaf nodes (path length > 1)
            // path[0] = group name ('Cell Types'), path[1] = phenotype name
            if (path?.length > 1) {
                colorByName[path[path.length - 1]] = color;
            }
        });
        // Build phenotype index → color array
        // Phenotypes are stored as sorted alphabetical indices in binary
        const uniqueNames = Object.keys(colorByName).sort();
        const phenotypeColors = uniqueNames.map(name => colorByName[name] ?? defaultColor);
        if (isFiltered) {
            // Build selected indices from obsSetSelection
            const selectedNames = new Set(obsSetSelection?.map(path => path[path.length - 1]) ?? []);
            const selectedIndices = uniqueNames
                .map((name, idx) => (selectedNames.has(name) ? idx : -1))
                .filter(idx => idx >= 0);
            if (selectedIndices.length === 0) {
                // Nothing selected — hide all points
                return 'void main() { discard; }';
            }
            return getObsSetSelectionFilteredShader(phenotypeColors, selectedIndices, opacity, pointMarkerBorderWidth);
        }
        return getObsSetSelectionShader(phenotypeColors, opacity, pointMarkerBorderWidth);
    }
    // Fallback: static color.
    return getSpatialLayerColorShader(staticColor, opacity, pointMarkerBorderWidth);
}
//# sourceMappingURL=shader-utils.js.map